// Transcript fetching in the browser ("client mode": always for signed-out users, optional for
// signed-in users, and the automatic fallback when YouTube blocks the server).
//
// The work is done by fetchVideoTranscriptInBrowser() (youtubeTranscript.ts: direct InnerTube
// calls, sandboxed-iframe relay where the origin is not CORS-allowed, e.g. localhost in dev).
// This module only maps its YouTubeError to BrowserTranscriptError with a German user message.

import type { VideoTranscript } from '../../shared/api';
import { YouTubeError, type YouTubeErrorReason } from '../../shared/youtube';
import { fetchVideoTranscriptInBrowser } from './youtubeTranscript';

export type BrowserTranscriptErrorCode =
  /** The video has no usable captions (none at all, live stream, age/members restriction). */
  | 'transcript_unavailable'
  /** The video does not exist or is private. */
  | 'video_not_found'
  /** YouTube refused the request (rate limit, bot check; a 429 also surfaces as a CORS/network failure). */
  | 'blocked'
  /** Anything else: offline, unexpected response. */
  | 'failed';

export class BrowserTranscriptError extends Error {
  readonly code: BrowserTranscriptErrorCode;
  /** German text for the UI (the technical `message` goes to "Technische Details"). */
  readonly userMessage: string | undefined;
  readonly reason: YouTubeErrorReason | undefined;

  constructor(
    code: BrowserTranscriptErrorCode,
    message: string,
    options: { userMessage?: string; reason?: YouTubeErrorReason; cause?: unknown } = {},
  ) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'BrowserTranscriptError';
    this.code = code;
    this.userMessage = options.userMessage;
    this.reason = options.reason;
  }
}

export const BLOCKED_MESSAGE = 'YouTube blockiert gerade Anfragen von deiner Verbindung – bitte später erneut versuchen.';

export async function fetchTranscriptInBrowser(
  videoId: string,
  options: { signal?: AbortSignal; preferredLang?: string } = {},
): Promise<VideoTranscript> {
  try {
    if (__MOCK_API__) {
      const { mockBrowserTranscript } = await import('../dev/mockBrowser');
      return await mockBrowserTranscript(videoId, options.signal);
    }
    return await fetchVideoTranscriptInBrowser(videoId, {
      ...(options.signal ? { signal: options.signal } : {}),
      ...(options.preferredLang ? { preferredLang: options.preferredLang } : {}),
    });
  } catch (error) {
    throw toBrowserTranscriptError(error, options.signal);
  }
}

/** Maps a failure of the YouTube layer; abort errors are passed through unchanged. */
export function toBrowserTranscriptError(error: unknown, signal?: AbortSignal): unknown {
  if (signal?.aborted || isAbort(error)) return error;
  if (error instanceof BrowserTranscriptError) return error;
  if (!(error instanceof YouTubeError)) {
    return new BrowserTranscriptError('failed', error instanceof Error ? error.message : String(error), { cause: error });
  }
  const make = (code: BrowserTranscriptErrorCode, userMessage?: string) =>
    new BrowserTranscriptError(code, `${error.reason}: ${error.message}`, {
      reason: error.reason,
      cause: error,
      ...(userMessage ? { userMessage } : {}),
    });

  switch (error.reason) {
    case 'invalid_id':
    case 'not_found':
      return make('video_not_found');
    case 'no_captions':
      return make('transcript_unavailable');
    case 'live':
      return make('transcript_unavailable', 'Das ist ein laufender oder geplanter Livestream. Ein Transkript gibt es erst, wenn er vorbei ist.');
    case 'login_required':
      return make(
        'transcript_unavailable',
        'Dieses Video ist altersbeschränkt oder nur für Kanalmitglieder. Ohne YouTube-Anmeldung lässt sich das Transkript nicht laden.',
      );
    case 'rate_limited':
    case 'bot_check':
    case 'po_token_required':
      return make('blocked', BLOCKED_MESSAGE);
    case 'network':
      // A 429 on the caption download comes without CORS headers: the browser only sees a failed fetch.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return make('failed', 'Keine Internetverbindung. Prüfe deine Verbindung und versuche es erneut.');
      }
      if (isTimeout(error)) return make('failed', 'YouTube hat nicht rechtzeitig geantwortet. Bitte versuche es erneut.');
      return make('blocked', BLOCKED_MESSAGE);
    case 'bad_response':
    default:
      return make('failed');
  }
}

function isTimeout(error: YouTubeError): boolean {
  const cause = error.cause as { name?: unknown } | undefined;
  return cause?.name === 'TimeoutError' || /did not answer in time/i.test(error.message);
}

function isAbort(error: unknown): boolean {
  const name = (error as { name?: unknown } | null)?.name;
  return name === 'AbortError';
}
